import {CardFace} from '../types/flipcardTypes';
import {I18nStrings} from '../i18n';
import {createEmptyCard} from './content';
import {ContentHandlers} from './contentHandlers';
import {
  flipCardClass, flipCardContainerClass,
  flipCardContentClass, flipCardContentOptionsClass,
  flipCardFaceClass,
  flipCardResetBtnClass,
  flipCardTitleClass
} from "@/components/tinyMce/plugins/flipcard/styles";
import {
  flipcardCardContentTypeAttr, flipcardCardFaceAttr,
  flipcardCardIdAttr,
  flipcardCardNumberAttr
} from "@/components/tinyMce/plugins/flipcard/flipcardConstants";

export function registerCommands(
  editor: any,
  i18n: I18nStrings,
  contentHandlers: ContentHandlers
): void {
  // Commande: Insérer une nouvelle ca rd
  editor.addCommand('flipcardInsert', () => {
    const html = createEmptyCard(i18n);
    editor.insertContent(html);

    // Attacher les événements à la nouvelle card
    setTimeout(() => {
      updateAllCardNumbers(editor, i18n);
      const cards = editor.dom.select(flipCardContainerClass);
      const newCard = cards[cards.length - 1] as HTMLElement;
      if (newCard) {
        contentHandlers.rebindEventListenersToAllCards();
      }
    }, 100);
  });

  // Commande: Supprimer une card
  editor.addCommand('flipcardDelete', (_ui: boolean, cardId: string) => {
    const container = editor.dom.select(`[${flipcardCardIdAttr}="${cardId}"]`)[0] as HTMLElement;
    if (container) {
      container.remove();
      // Renuméroter les cards restantes
      updateAllCardNumbers(editor, i18n);
    }
  });

  editor.addCommand('flipcardCopy', (_ui: boolean, cardId: string) => {
    const container = editor.dom.select(`[${flipcardCardIdAttr}="${cardId}"]`)[0].parentElement as HTMLElement;
    if (container) {
      const clonedCard = container.cloneNode(true) as HTMLElement;
      // Générer un nouvel ID
      const newCardId = `flipcard-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      (clonedCard.firstElementChild! as HTMLElement).setAttribute(flipcardCardIdAttr, newCardId);

      container.insertAdjacentElement('afterend', clonedCard);

      contentHandlers.attachEventListeners(clonedCard.firstElementChild! as HTMLElement);

      // Afficher le bouton Reset si nécessaire
      const activeFace = (clonedCard.firstElementChild! as HTMLElement).querySelector('.flipcard-face.active') as HTMLElement;
      const content = activeFace?.querySelector('.flipcard-content') as HTMLElement;

      if (content?.classList.contains('has-content')) {
        const resetBtn = (clonedCard.firstElementChild! as HTMLElement).querySelector('.flipcard-btn-reset') as HTMLElement;
        if (resetBtn) {
          resetBtn.style.display = 'flex';
        }
      }

      updateAllCardNumbers(editor, i18n);
    }
  });

  // Commande: Réinitialiser une face
  editor.addCommand('flipcardReset', (_ui: boolean, data: { cardId: string; face: CardFace }) => {
    const container = editor.dom.select(`[${flipcardCardIdAttr}="${data.cardId}"]`)[0] as HTMLElement;

    if (container) {
      contentHandlers.resetFace(container, data.face);
    }
  });

  // Commande: Retourner une card
  editor.addCommand('flipcardFlip', (_ui: boolean, cardId: string) => {
    const container = editor.dom.select(`[${flipcardCardIdAttr}="${cardId}"]`)[0] as HTMLElement;
    if (container) {
      const flipBtn = container.querySelector('.flipcard-btn-flip') as HTMLElement;
      if (flipBtn) {
        flipBtn.click();
      }
    }
  });

// Commande: Insérer du texte
  editor.addCommand('flipcardInsertText', (_ui: boolean, data: { cardId: string; face: CardFace; text: string }) => {
    const container = editor.dom.select(`[${flipcardCardIdAttr}="${data.cardId}"]`)[0] as HTMLElement;

    if (container) {
      const faceElement = container.querySelector(`${flipCardClass}-${data.face}`) as HTMLElement;
      if (!faceElement) return;

      const options = faceElement.querySelector(flipCardContentOptionsClass) as HTMLElement;
      const content = faceElement.querySelector(flipCardContentClass) as HTMLElement;

      if (options && content) {
        options.classList.add('hidden');
        content.classList.add('has-content');
        content.setAttribute(flipcardCardContentTypeAttr, 'text');
        if (data.text.trim() === '') {
          content.innerHTML = `<div contenteditable="true" class="flipcard-text-content" data-placeholder="${i18n.addTextPlaceholder}"></div>`;
        } else {
          content.innerHTML = `<div contenteditable="true" class="flipcard-text-content">${data.text}</div>`;
        }
        displayResetButtonAndFocusTextZone(faceElement, container, content);
      }
    }
  });

  function displayResetButtonAndFocusTextZone(faceElement: HTMLElement, container: HTMLElement, content: HTMLElement) {
    // Afficher le bouton Reset si c'est la face active
    const isActive = faceElement.classList.contains('active');
    if (isActive) {
      const resetBtn = container.querySelector(flipCardResetBtnClass) as HTMLElement;
      if (resetBtn) {
        resetBtn.classList.add('visible');
      }
    }

    // Focus sur le div éditable
    const editableDiv = content.querySelector('.flipcard-text-content') as HTMLElement;
    if (editableDiv) {
      editableDiv.focus();
    }
  }

  // Commande: Insérer une image
  editor.addCommand('flipcardInsertImage', (_ui: boolean, data: { cardId: string; face: CardFace; imageUrl: string }) => {
    const container = editor.dom.select(`[${flipcardCardIdAttr}="${data.cardId}"]`)[0] as HTMLElement;

    if (container) {
      contentHandlers.insertImage(container, data.face, data.imageUrl);
    }
  });
}

function updateAllCardNumbers(editor: any, i18n: I18nStrings): void {
  const cards = editor.dom.select('.flipcard-container');

  cards.forEach((card: HTMLElement, index: number) => {
    const cardNumber = index + 1;
    card.setAttribute(flipcardCardNumberAttr, cardNumber.toString());

    const titleElement = card.querySelector(flipCardTitleClass);
    if (titleElement) {
      const activeFace = card.querySelector(`${flipCardFaceClass}.active`) as HTMLElement;
      const face = activeFace?.getAttribute(flipcardCardFaceAttr) as CardFace || 'front';
      titleElement.textContent = i18n.cardTitle(cardNumber, face);
    }
  });
}

